import clsx from 'clsx';
import { ComponentProps, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { LocaleContext } from './Layout';

export const H1 = ({
  as = 'h1',
  children,
  size = 'md',
  ...props
}: ComponentProps<'h1'> & {
  as?: 'h1' | 'h2';
  className?: string;
  size?: 'md' | 'xl' | '2xl';
  headingStyle?: boolean;
}) => {
  if (size === '2xl') {
    return (
      <Display3XL el={as} {...props}>
        {children}
      </Display3XL>
    );
  }

  if (size === 'xl') {
    return (
      <Display2XL el={as} {...props}>
        {children}
      </Display2XL>
    );
  }

  return (
    <DisplayXL el={as} {...props}>
      {children}
    </DisplayXL>
  );
};

export const H2 = ({ children, ...props }: ComponentProps<'h2'> & { className?: string; headingStyle?: boolean }) => {
  return <DisplayLG {...props}>{children}</DisplayLG>;
};

export const H3 = ({ children, ...props }: ComponentProps<'h3'> & { className?: string; headingStyle?: boolean }) => {
  return <DisplayMD {...props}>{children}</DisplayMD>;
};

export const H4 = ({ children, ...props }: ComponentProps<'h4'> & { className?: string; headingStyle?: boolean }) => {
  return <DisplaySM {...props}>{children}</DisplaySM>;
};

export const P = ({ children, className, ...props }: { className?: string } & ComponentProps<'p'>) => (
  <p className={twMerge(clsx('text-balance text-md font-normal leading-normal md:text-lg', className))} {...props}>
    {children}
  </p>
);

export const PSmall = ({ children, className, ...props }: { className?: string } & ComponentProps<'p'>) => (
  <p
    className={twMerge(clsx('small text-balance text-xs font-normal leading-normal md:text-sm', className))}
    {...props}
  >
    {children}
  </p>
);

export const Display3XL = ({
  children,
  className,
  el = 'h1',
  ...props
}: { className?: string; el?: 'h1' | 'h2' } & ComponentProps<'h1'>) => {
  const locale = useContext(LocaleContext);
  const El = el;
  return (
    <El
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[48px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[96px] md:tracking-[-0.44px]',
          locale === 'fr' && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </El>
  );
};

export const Display2XL = ({
  children,
  className,
  el = 'h1',
  headingStyle,
  ...props
}: { className?: string; el?: 'h1' | 'h2'; headingStyle?: boolean } & ComponentProps<'h1'>) => {
  const locale = useContext(LocaleContext);
  const El = el;
  return (
    <El
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[48px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[72px] md:tracking-[-0.44px]',
          headingStyle && 'font-basel-grotesk font-medium normal-case leading-none tracking-normal',
          locale === 'fr' && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </El>
  );
};

export const DisplayXL = ({
  children,
  className,
  el = 'h2',
  headingStyle = false,
  ...props
}: { className?: string; el?: 'h1' | 'h2'; headingStyle?: boolean } & ComponentProps<'h2'>) => {
  const locale = useContext(LocaleContext);
  const El = el;
  return (
    <El
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[36px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[60px] md:tracking-[-0.44px]',
          headingStyle &&
            'font-basel-grotesk text-[42px] font-medium normal-case leading-none tracking-normal md:text-[60px]',
          locale === 'fr' && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </El>
  );
};

export const DisplayLG = ({
  children,
  className,
  headingStyle = false,
  ...props
}: { className?: string; headingStyle?: boolean } & ComponentProps<'h2'>) => {
  const locale = useContext(LocaleContext);
  return (
    <h2
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[30px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[48px]',
          headingStyle &&
            'font-basel-grotesk text-[32px] font-medium normal-case leading-129 tracking-normal md:text-[42px] md:leading-none',
          locale === 'fr' && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </h2>
  );
};

const DisplayMD = ({
  children,
  className,
  headingStyle = false,
  ...props
}: { className?: string; headingStyle?: boolean } & ComponentProps<'h3'>) => {
  const locale = useContext(LocaleContext);
  return (
    <h3
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[30px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[36px]',
          headingStyle &&
            'font-basel-grotesk text-[32px] font-medium normal-case leading-129 tracking-normal md:text-[42px] md:*:leading-none',
          locale === 'fr' && !headingStyle && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </h3>
  );
};

export const DisplaySM = ({
  children,
  className,
  headingStyle = false,
  ...props
}: { className?: string; headingStyle?: boolean } & ComponentProps<'h4'>) => {
  const locale = useContext(LocaleContext);
  return (
    <h4
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[24px] font-bold uppercase leading-90 tracking-[-0.12px] md:text-[30px]',
          headingStyle &&
            'font-basel-grotesk text-[20px] font-medium normal-case leading-129 tracking-normal md:text-[32px]',
          locale === 'fr' && !headingStyle && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </h4>
  );
};

export const DisplayXS = ({
  children,
  className,
  headingStyle = false,
  ...props
}: { className?: string; headingStyle?: boolean } & ComponentProps<'h4'>) => {
  const locale = useContext(LocaleContext);
  return (
    <h4
      className={twMerge(
        clsx(
          'text-balance font-jokker text-[24px] font-bold uppercase leading-90 tracking-[-0.12px]',
          headingStyle && 'font-basel-grotesk text-[20px] font-medium normal-case leading-129 tracking-normal',
          locale === 'fr' && !headingStyle && 'leading-none',
          className,
        ),
      )}
      {...props}
    >
      {children}
    </h4>
  );
};
